import type { CabinetCaseDetail, CaseDocument, CaseEvent, ChecklistItem, LocText } from '../../api/types';
import type { DictKey, Vars } from '../../i18n';
import { loc } from '../../lib/loc';

/** Items the family has to provide (responsible=client), in checklist order. not_needed is hidden. */
export function clientItems(items: ChecklistItem[]): ChecklistItem[] {
  return items.filter((i) => i.responsible === 'client' && i.status !== 'not_needed').sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

/** Still waiting on the client: missing or rejected and required. */
export function openItems(items: ChecklistItem[]): ChecklistItem[] {
  return clientItems(items).filter((i) => i.required && (i.status === 'missing' || i.status === 'rejected'));
}

/** Why a rejected file has to be sent again (the manager's note); null for other statuses. */
export function rejectReason(i: ChecklistItem): string | null {
  if (i.status !== 'rejected') return null;
  return i.note?.trim() || i.reject_reason || i.rejection_reason || i.comment || null;
}

/**
 * Яку позначку показати біля пункту: необов'язковий документ, якого ще немає, — «За бажанням», а не «Потрібно».
 * Для решти статусів позначка однакова незалежно від того, обов'язковий пункт чи ні.
 */
export function badgeKey(i: Pick<ChecklistItem, 'status' | 'required'>): DictKey {
  if (i.status === 'missing' && !i.required) return 'status.optional';
  if (i.status === 'uploaded') return 'status.uploaded';
  if (i.status === 'accepted') return 'status.accepted';
  if (i.status === 'rejected') return 'status.rejected';
  return 'status.missing';
}

const CLIENT_KINDS = new Set(['contact', 'client', 'customer']);

/**
 * Split documents into "from us" and "sent by you".
 * Uses `uploaded_by_kind` / `source` when present, otherwise falls back to which documents are attached
 * to the client's own checklist items.
 */
export function splitDocuments(d: Pick<CabinetCaseDetail, 'documents' | 'checklist'>): { fromUs: CaseDocument[]; yours: CaseDocument[] } {
  const clientDocIds = new Set(clientItems(d.checklist).map((i) => i.document_id).filter(Boolean));
  const fromUs: CaseDocument[] = [];
  const yours: CaseDocument[] = [];
  for (const doc of d.documents) {
    if (doc.status === 'pending' || doc.status === 'rejected') {
      if (isClientDoc(doc, clientDocIds)) yours.push(doc);
      continue; // half-uploaded or rejected staff files are not shown
    }
    (isClientDoc(doc, clientDocIds) ? yours : fromUs).push(doc);
  }
  return { fromUs, yours };
}

function isClientDoc(doc: CaseDocument, clientDocIds: Set<string | null>): boolean {
  if (doc.uploaded_by_kind) return CLIENT_KINDS.has(doc.uploaded_by_kind);
  if (doc.source === 'generated') return false;
  return clientDocIds.has(doc.id);
}

export function docTitle(doc: CaseDocument, locale: string): string {
  return loc(doc.document_type?.name, locale) || doc.file_name;
}

export interface EventText {
  key: DictKey;
  vars?: Vars;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v ? v : undefined;
}

/**
 * Turn a client-visible case event into a calm one-line description.
 * Event types are not fixed in the contract yet, so we match by meaning.
 */
export function describeEvent(
  ev: CaseEvent,
  ctx: {
    locale: string;
    stateName: (key: string) => string | undefined;
    docName: (key: string) => string | undefined;
    /** Label of a family step (step.<key>); unknown keys → undefined. */
    stepName?: (key: string) => string | undefined;
  },
): EventText {
  const type = ev.type.toLowerCase();
  const p = (ev.payload ?? {}) as Record<string, unknown>;
  if (type === 'case_created' || type === 'case.created') return { key: 'event.case_created' };

  // крок для родини (батч p5-A): лише назва кроку — без місць, партнерів і фото
  if (type === 'family_step_reached') {
    const k = str(p.step_key);
    const name = k ? ctx.stepName?.(k) : undefined;
    return name ? { key: 'event.step', vars: { step: name } } : { key: 'event.generic' };
  }

  if (/state|transition|stage/.test(type)) {
    const key = str(p.to) ?? str(p.to_key) ?? str(p.state) ?? str(p.state_key);
    const name = (key && ctx.stateName(key)) || loc(p.to_name as LocText, ctx.locale) || loc(p.state_name as LocText, ctx.locale);
    return name ? { key: 'event.state_changed', vars: { state: name } } : { key: 'event.state_changed_plain' };
  }

  if (/doc|checklist|file/.test(type)) {
    // кілька документів одного статусу, зведені сервером в один рядок (GET /cabinet/cases/{id})
    if (Array.isArray(p.document_types) && p.document_types.length > 1) {
      const names = (p.document_types as unknown[])
        .map((x) => (typeof x === 'string' ? ctx.docName(x) : undefined))
        .filter((x): x is string => !!x);
      const st = `${str(p.to) ?? ''}`;
      if (names.length && /accept/.test(st)) return { key: 'event.documents_accepted', vars: { docs: names.join(', ') } };
      if (names.length && /upload|received/.test(st)) return { key: 'event.documents_uploaded', vars: { docs: names.join(', ') } };
    }
    const k =str(p.document_type_key) ?? str(p.document_type) ?? str(p.type_key) ?? str(p.key);
    const doc = (k && ctx.docName(k)) || loc(p.document_type_name as LocText, ctx.locale) || str(p.file_name);
    const status = `${type} ${str(p.status) ?? ''} ${str(p.to) ?? ''}`;
    const pick = (): DictKey | null => {
      if (/reject/.test(status)) return 'event.document_rejected';
      if (/accept/.test(status)) return 'event.document_accepted';
      if (/generat|ready|prepared|issued/.test(status)) return 'event.document_ready';
      if (/upload|received|complete/.test(status)) return 'event.document_uploaded';
      return null;
    };
    const key = pick();
    return key && doc ? { key, vars: { doc } } : { key: 'event.document_plain' };
  }

  if (/order/.test(type)) {
    if (/ship/.test(type)) return { key: 'event.order_shipped' };
    if (/complet/.test(type)) return { key: 'event.order_completed' };
  }
  if (/message|chat/.test(type)) return { key: 'event.message' };
  return { key: 'event.generic' };
}
